import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import * as Yup from 'yup';
// Components
import { Close, Save } from '@mui/icons-material';
import { Box, Button, Fade, Grid, TextField, Typography } from '@mui/material';
import { Loader, Modal } from '../../../../components';
import { createDocumentType, updateDocumentType } from '../../../../fetch';
import { IDocumentType } from '../../../../models';

interface IDocumentTypeModal {
  open: boolean;
  onClose: () => void;
  currentDocumentType?: IDocumentType;
  fetchDocumentTypes: () => {};
}

const DocumentTypeSchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required')
});

export const DocumentTypeModal: FC<IDocumentTypeModal> = ({
  open,
  onClose,
  currentDocumentType,
  fetchDocumentTypes
}) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentDocumentType && currentDocumentType.name ? currentDocumentType.name : ''
        }}
        validationSchema={DocumentTypeSchema}
        onSubmit={async (values, actions) => {
          try {
            let res = null;
            if (currentDocumentType && currentDocumentType.documentTypeId) {
              res = await updateDocumentType(currentDocumentType.documentTypeId, values);
            } else {
              res = await createDocumentType(values);
            }
            if (res && res.Status >= 400) {
              if (
                res &&
                res?.Errors &&
                Object.values(res.Errors)[0] &&
                Object.values(Object.values(res.Errors)[0])[0]
              ) {
                enqueueSnackbar(Object.values(Object.values(res.Errors)[0])[0] as string, {
                  variant: 'error'
                });
              } else {
                enqueueSnackbar(
                  `Error ${
                    currentDocumentType ? 'updating' : 'creating'
                  } this document type. Please try again.`,
                  {
                    variant: 'error'
                  }
                );
              }
            } else {
              enqueueSnackbar(
                currentDocumentType ? 'Document Type Updated!' : 'Document Type Created!',
                {
                  variant: 'success'
                }
              );
              onClose();
              fetchDocumentTypes();
              actions.resetForm();
            }
          } catch (error) {
            console.log(error);
            if (
              error &&
              error.Errors &&
              Object.values(error.Errors)[0] &&
              Object.values(Object.values(error.Errors)[0])[0]
            ) {
              enqueueSnackbar(Object.values(Object.values(error.Errors)[0])[0] as string, {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                }
              }}
              maxWidth="xs"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="1rem">
                    <Typography variant="h5">
                      {currentDocumentType ? `Edit Document Type` : 'Add Document Type'}
                    </Typography>
                  </Box>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Name"
                        name="name"
                        value={values.name}
                        onBlur={handleBlur}
                        size="small"
                        onChange={e => setFieldValue('name', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
